import { type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  useCanGoBack,
  useNavigate,
  useRouter,
  Link,
} from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * PageHeader — единый header для всех страниц frontend-vite.
 *
 * Закрывает Nielsen heuristic 3 (User control & freedom) — даёт
 * стабильный escape-pattern: back-button + breadcrumbs на каждой странице.
 *
 * Use cases:
 * - Detail pages (/fields/:id, /operations/:id) — back + title + actions
 * - Sub-pages (/settings/profile) — breadcrumbs + title
 * - Root pages (/dashboard, /map, /fields) — без back, только title + actions
 *
 * **Placement contract (AK-127 era, in-flow):** PageHeader рендерится
 * **в потоке контента** внутри `AppShell <main>` (который уже даёт
 * `p-3/p-6`). По умолчанию — без `border-b` (drop-in замена `<h1>`).
 * Для full-width bar-варианта (Figma component_set «bar») — пропс
 * `bordered`. Future option (если AppShell получит header-slot): full-bleed.
 *
 * Source of truth: frontend-vite/CLAUDE.md (DS rules), Figma 03 — Components.
 */

export interface BreadcrumbItem {
  /** Видимый текст */
  label: string;
  /**
   * Куда вести клик. Если не задан — это «текущий» элемент (последний в цепочке),
   * рендерится как text без ссылки.
   */
  to?: string;
  /**
   * Опциональные search-параметры, проксируются в TanStack Router `<Link>`.
   * Используются для filter-навигации (например breadcrumb «cluster_name» →
   * `/fields?cluster_id=X`).
   */
  search?: Record<string, unknown>;
}

export interface PageHeaderProps {
  /** Заголовок страницы. Обязательный — если бы был optional, лучше не использовать PageHeader. */
  title: string;
  /** Опциональный заголовок-suffix (например «№42» рядом с именем). Рендерится более лёгким стилем. */
  titleSuffix?: ReactNode;
  /** Опциональное описание под title. */
  description?: string;
  /**
   * Показывать ли back-button слева. По умолчанию — `true`.
   * Установи `false` для root pages (`/dashboard`, `/map`, `/fields`),
   * куда «назад» не имеет смысла.
   */
  showBack?: boolean;
  /** Текст back-кнопки. По умолчанию — «Назад». */
  backLabel?: string;
  /**
   * Explicit back-target (TanStack route path). Если задан → клик
   * `navigate({ to: backTo })` (предсказуемый «к списку»).
   * Если не задан → `router.history.back()` (browser-back, generic).
   *
   * Backwards-compat: ранее (PR #144) был только history.back(). `backTo`
   * добавлен 2026-05-19 для detail-страниц где нужен фиксированный
   * back-маршрут (например `/fields/:id` → `/fields`).
   */
  backTo?: string;
  /**
   * Хлебные крошки над заголовком. Показываются только если массив non-empty.
   * Последний элемент считается текущей страницей и рендерится без ссылки.
   * Поддерживает `search`-параметры для filter-навигации.
   */
  breadcrumbs?: BreadcrumbItem[];
  /** Слот действий справа: Button(s), DropdownMenu, etc. */
  actions?: ReactNode;
  /**
   * Опциональный bar-mode: добавляет `border-b` снизу и явный `bg-background`
   * (для full-width header-бара, если AppShell когда-нибудь получит
   * header-slot). По умолчанию `false` — header в потоке, drop-in для `<h1>`.
   */
  bordered?: boolean;
  /** Кастомный className на корневой `<header>`. */
  className?: string;
}

export function PageHeader({
  title,
  titleSuffix,
  description,
  showBack = true,
  backLabel = 'Назад',
  backTo,
  breadcrumbs,
  actions,
  bordered = false,
  className,
}: PageHeaderProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  // Если backTo задан — back-button рендерится всегда (explicit destination доступна).
  // Иначе — только если history-back имеет смысл (canGoBack).
  const showBackButton = showBack && (backTo !== undefined || canGoBack);

  const handleBack = () => {
    if (backTo !== undefined) {
      navigate({ to: backTo });
    } else {
      router.history.back();
    }
  };

  return (
    <header
      className={cn(
        'flex flex-col gap-2',
        bordered && 'border-b border-border bg-background px-6 py-4',
        className,
      )}
    >
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Хлебные крошки">
          <ol className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
            {breadcrumbs.map((item, idx) => {
              const isLast = idx === breadcrumbs.length - 1;
              return (
                <li key={`${item.label}-${idx}`} className="flex items-center gap-1">
                  {item.to && !isLast ? (
                    <Link
                      to={item.to}
                      search={item.search as never}
                      className="hover:text-foreground transition-colors"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span
                      className={cn(isLast && 'text-foreground font-medium')}
                      aria-current={isLast ? 'page' : undefined}
                    >
                      {item.label}
                    </span>
                  )}
                  {!isLast && (
                    <ChevronRight
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60"
                      aria-hidden
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {showBackButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              aria-label={backLabel}
              className="shrink-0 -ml-2"
            >
              <ChevronLeft />
              <span className="hidden sm:inline">{backLabel}</span>
            </Button>
          )}

          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-semibold leading-tight text-foreground">
              <span className="truncate">{title}</span>
              {titleSuffix && (
                <span className="ml-3 text-base text-muted-foreground font-normal">
                  {titleSuffix}
                </span>
              )}
            </h1>
            {description && (
              /* Описание НЕ обрезаем: заголовок можно (он опознаётся по началу),
                 а подзаголовок — единственное объяснение экрана, и в обрезанном
                 виде («…не трогают факт прошло…») он бесполезен. Design review
                 2026-08-18, зона 2 · А6. */
              <p className="mt-1 text-sm text-muted-foreground">{description}</p>
            )}
          </div>
        </div>

        {actions && (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>
    </header>
  );
}
